import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useEffect } from 'react';
import { merge } from 'lodash';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { palette } from './palette';
import { componentsOverrides } from './overrides';
import { customShadows } from './custom-shadows';
import { shadows } from './shadows';
import { typography } from './typography';
// import { ThemeContext, ThemeContextType } from '@application/context/themeContext';
import { breakpoints } from './breakpoints';
import { useAppSelector } from '@application/store/useStore';
import { applyCompanyTheme, injectFontFaceCSS } from './theme-utils';
export default function CustomThemeProvider({ children }) {
    // const { theme: selectedTheme } = useContext(ThemeContext) as ThemeContextType;
    const { currentTheme, isThemeApplied } = useAppSelector((state) => state.companyTheme);
    const baseOption = useMemo(() => ({
        palette: palette('light'),
        shadows: shadows('light'),
        customShadows: customShadows('light'),
        typography,
        shape: { borderRadius: 8 },
        breakpoints
    }), []);
    // Get company information for theme customization
    const { selectedCompanyId, companies } = useAppSelector((state) => state.company);
    const selectedCompany = companies.find((c) => selectedCompanyId.includes(c.id));
    // Check if multiple companies are selected - use VDK Groep theme in that case
    const isMultipleCompaniesSelected = selectedCompanyId.length > 1;
    const vdkGroepCompany = companies.find((c) => c.name?.toLowerCase().includes('vdk groep') ||
        c.name?.toLowerCase().includes('vdkgroep') ||
        c.name?.toLowerCase() === 'vdk groep');
    // Use VDK Groep company when multiple companies are selected
    const themeCompany = isMultipleCompaniesSelected ? vdkGroepCompany : selectedCompany;
    const themeCompanyName = isMultipleCompaniesSelected ? 'VDK Groep' : selectedCompany?.name;
    // Apply company theme customizations if available
    // If no theme or not applied, use base defaults
    const themedOption = useMemo(() => {
        if (currentTheme && isThemeApplied) {
            return applyCompanyTheme(baseOption, currentTheme, selectedCompanyId[0], themeCompanyName);
        }
        // Return base theme with defaults when no company theme
        return baseOption;
    }, [baseOption, currentTheme, isThemeApplied, selectedCompanyId, themeCompanyName]);
    const memoizedValue = useMemo(() => merge(
    // Base
    themedOption), [themedOption]);
    const theme = useMemo(() => {
        const builtTheme = createTheme(memoizedValue);
        builtTheme.components = merge(componentsOverrides(builtTheme, themeCompany, currentTheme));
        return builtTheme;
    }, [memoizedValue, themeCompany, currentTheme]);
    // Apply background image and other CSS-based theme properties
    useEffect(() => {
        if (currentTheme?.backgroundImageUrl && isThemeApplied) {
            document.body.style.backgroundImage = `url(${currentTheme.backgroundImageUrl})`;
            document.body.style.backgroundSize = 'cover';
            document.body.style.backgroundAttachment = 'fixed';
            document.body.style.backgroundPosition = 'center';
        }
        else {
            // Reset to default when no background image
            document.body.style.backgroundImage = '';
            document.body.style.backgroundSize = '';
            document.body.style.backgroundAttachment = '';
            document.body.style.backgroundPosition = '';
        }
        return () => {
            document.body.style.backgroundImage = '';
            document.body.style.backgroundSize = '';
            document.body.style.backgroundAttachment = '';
            document.body.style.backgroundPosition = '';
        };
    }, [currentTheme, isThemeApplied]);
    // Inject dynamic font-face CSS based on company font family
    // This must happen immediately when theme changes to ensure fonts are loaded
    useEffect(() => {
        // Inject font-face CSS as soon as we have a fontFamily, regardless of isThemeApplied
        // This ensures fonts are available when the theme applies them
        if (currentTheme?.fontFamily) {
            injectFontFaceCSS(currentTheme.fontFamily);
        }
        else {
            // Remove dynamic font CSS when no font family is set
            injectFontFaceCSS(null);
        }
        return () => {
            // Cleanup: remove dynamic font CSS on unmount
            injectFontFaceCSS(null);
        };
    }, [currentTheme?.fontFamily]); // Removed isThemeApplied dependency to inject fonts immediately
    return (_jsxs(ThemeProvider, { theme: theme, children: [_jsx(CssBaseline, {}), children] }));
}
